const initShop = (root) => {
  if (!(root instanceof HTMLElement) || root.dataset.scShopReady === 'true') return;

  const cards = [...root.querySelectorAll('[data-sc-shop-card]')];
  const typeButtons = [...root.querySelectorAll('[data-sc-shop-type-control]')];
  const colourButtons = [...root.querySelectorAll('[data-sc-shop-colour-control]')];
  const count = root.querySelector('[data-sc-shop-count]');

  if (!cards.length || !colourButtons.length || !count) return;

  root.dataset.scShopReady = 'true';

  const hasType = (type) => cards.some((card) => card.dataset.scShopType === type);
  let type = root.dataset.scShopDefaultType || 'set';
  if (!hasType(type)) type = hasType('piece') ? 'piece' : 'set';
  let colour = root.dataset.scShopDefaultColour || 'all';

  const labelForCards = (visibleCards) => {
    const amount = visibleCards.length;
    if (type === 'set') return `${amount} ${amount === 1 ? 'set' : 'sets'}`;

    const productTypes = new Set(visibleCards.map((card) => card.dataset.scShopProductType));
    if (productTypes.size === 1 && productTypes.has('set')) {
      return `${amount} complete ${amount === 1 ? 'set' : 'sets'}`;
    }
    if (productTypes.size === 1 && productTypes.has('piece')) {
      return `${amount} individual ${amount === 1 ? 'piece' : 'pieces'}`;
    }
    return `${amount} ${amount === 1 ? 'style' : 'styles'}`;
  };

  const render = () => {
    const availableColours = new Set(
      cards.filter((card) => card.dataset.scShopType === type).map((card) => card.dataset.scShopColour)
    );

    if (colour !== 'all' && !availableColours.has(colour)) colour = 'all';

    const visibleCards = [];
    for (const card of cards) {
      const show =
        card.dataset.scShopType === type &&
        (colour === 'all' || card.dataset.scShopColour === colour);
      card.closest('li').hidden = !show;
      if (show) visibleCards.push(card);
    }

    for (const button of typeButtons) {
      const buttonType = button.dataset.scShopTypeControl;
      button.disabled = !hasType(buttonType);
      button.setAttribute('aria-pressed', String(buttonType === type));
    }

    for (const button of colourButtons) {
      const buttonColour = button.dataset.scShopColourControl;
      button.disabled = buttonColour !== 'all' && !availableColours.has(buttonColour);
      button.setAttribute('aria-pressed', String(buttonColour === colour));
    }

    count.textContent = labelForCards(visibleCards);
  };

  for (const button of typeButtons) {
    button.addEventListener('click', () => {
      const nextType = button.dataset.scShopTypeControl;
      if (!hasType(nextType)) return;
      type = nextType;
      render();
    });
  }

  for (const button of colourButtons) {
    button.addEventListener('click', () => {
      if (button.disabled) return;
      colour = button.dataset.scShopColourControl;
      render();
    });
  }

  render();
};

const initAllShops = (scope = document) => {
  if (scope instanceof HTMLElement && scope.matches('[data-sc-shop]')) initShop(scope);
  scope.querySelectorAll?.('[data-sc-shop]').forEach(initShop);
};

const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
const SHOP_SELECTOR = '[data-sc-shop]';
const BAG_SELECTOR = '.sc-shop__bag';
const CONTEXT_CART_SELECTOR =
  '#header-component[data-sc-context-header="collection"] .sc-context-links--right a[href*="/cart"]';

let cartRefreshController;

/**
 * Refreshes only the server-rendered cart snapshots on the Shop page.
 * Product filters and their focus/pressed state stay in the current document.
 */
const refreshShopCartSnapshots = async () => {
  if (!document.querySelector(SHOP_SELECTOR)) return;

  cartRefreshController?.abort();
  const controller = new AbortController();
  cartRefreshController = controller;

  try {
    const response = await fetch(window.location.href, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: controller.signal,
    });

    if (!response.ok) throw new Error(`Shop refresh failed with status ${response.status}`);

    const snapshot = new DOMParser().parseFromString(await response.text(), 'text/html');
    if (controller.signal.aborted) return;

    const freshBag = snapshot.querySelector(`${SHOP_SELECTOR} ${BAG_SELECTOR}`);
    if (freshBag instanceof HTMLElement) {
      for (const root of document.querySelectorAll(SHOP_SELECTOR)) {
        const currentBag = root.querySelector(BAG_SELECTOR);
        if (currentBag instanceof HTMLElement) currentBag.replaceWith(freshBag.cloneNode(true));
      }
    }

    const currentContextCart = document.querySelector(CONTEXT_CART_SELECTOR);
    const freshContextCart = snapshot.querySelector(CONTEXT_CART_SELECTOR);
    if (currentContextCart instanceof HTMLAnchorElement && freshContextCart instanceof HTMLAnchorElement) {
      currentContextCart.textContent = freshContextCart.textContent;
    }
  } catch (error) {
    if (error?.name !== 'AbortError') console.warn('[sc-shop] Cart snapshot refresh failed:', error);
  } finally {
    if (cartRefreshController === controller) cartRefreshController = undefined;
  }
};

/** @param {Event & { promise?: Promise<unknown> }} event */
const onCartLinesUpdate = (event) => {
  if (event.promise && typeof event.promise.then === 'function') {
    event.promise
      .then(refreshShopCartSnapshots)
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sc-shop] Cart update rejected:', error);
      });
    return;
  }

  refreshShopCartSnapshots();
};

initAllShops();
document.addEventListener('shopify:section:load', (event) => initAllShops(event.target));
document.addEventListener(CART_LINES_UPDATE_EVENT, onCartLinesUpdate);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshShopCartSnapshots();
});
